import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, DatePicker, Drawer, Form, Input, Popconfirm, Select } from 'antd';
import dayjs from 'dayjs';
import { listSemestres, createSemestre, updateSemestre, deleteSemestre, getCurrentSemestre } from '../../api/semestres';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';
import { StatusTag, EditIcon, DeleteIcon } from '../../components/UIKit';

export default function Semestres() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [current, setCurrent] = useState(null);
  const [overrideSemestreId, setOverrideSemestreId] = useState(() => localStorage.getItem('semestre_id_override') || null);
  const [form] = Form.useForm();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [data, cur] = await Promise.all([listSemestres({}), getCurrentSemestre()]);
      setRows(data); setCurrent(cur);
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les semestres.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (overrideSemestreId) localStorage.setItem('semestre_id_override', String(overrideSemestreId));
    else localStorage.removeItem('semestre_id_override');
  }, [overrideSemestreId]);

  const onAdd = () => { setEditing(null); form.resetFields(); setOpen(true); };
  const onEdit = useCallback((record) => { setEditing(record); form.setFieldsValue({ ...record, range: [dayjs(record.date_debut), dayjs(record.date_fin)] }); setOpen(true); }, [form]);
  const onDelete = useCallback(async (record) => {
    try { await deleteSemestre(record.id); message.success('Semestre supprimé.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression du semestre échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { nom: values.nom, annee: Number(values.annee), date_debut: values.range?.[0]?.format('YYYY-MM-DD'), date_fin: values.range?.[1]?.format('YYYY-MM-DD') };
      if (editing) { await updateSemestre(editing.id, payload); message.success('Semestre modifié.'); }
      else { await createSemestre(payload); message.success('Semestre créé.'); }
      setOpen(false); fetchAll();
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Enregistrement du semestre échoué.'), placement: 'topRight' }); }
  };

  const archiveOptions = useMemo(() => [
    { label: 'Semestre actif (par défaut)', value: 'active' },
    ...rows.filter((r) => !(current && String(current.id) === String(r.id))).map((r) => ({ label: `Archive: ${r.nom} ${r.annee}`, value: `archive:${r.id}` })),
  ], [current, rows]);

  return (
    <div className="page">
      {current && (
        <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
          <span className="section-label">Semestre actif</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{current.nom} {current.annee} <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>({current.date_debut} → {current.date_fin})</span></p>
        </div>
      )}
      {overrideSemestreId && (
        <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 12 }}>
          <strong>Mode archive</strong> — consultation sur semestre_id={overrideSemestreId}
        </div>
      )}

      <TableCard
        label="Semestres"
        loading={loading}
        rows={rows}
        toolbar={<>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#888' }}>Mode données:</span>
            <Select
              style={{ width: 240 }}
              value={overrideSemestreId ? `archive:${overrideSemestreId}` : 'active'}
              onChange={(v) => { if (v === 'active') return setOverrideSemestreId(null); setOverrideSemestreId(String(v).replace(/^archive:/, '') || null); }}
              options={archiveOptions}
            />
          </div>
          <button className="btn-primary" onClick={onAdd}>+ Ajouter</button>
        </>}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Année</th>
              <th>Début</th>
              <th>Fin</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const isActive = current && String(current.id) === String(r.id);
              return (
                <tr key={r.id}>
                  <td>{r.nom}</td>
                  <td>{r.annee}</td>
                  <td>{r.date_debut}</td>
                  <td>{r.date_fin}</td>
                  <td><span className={`tag ${isActive ? 'tag-green' : 'tag-grey'}`}>{isActive ? 'Actif' : 'Inactif'}</span></td>
                  <td>
                    <div className="actions-cell">
                      <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                      <Popconfirm title="Supprimer ce semestre ?" description={`${r.nom} ${r.annee}`} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDelete(r)}>
                        <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                      </Popconfirm>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </TableCard>

      <Drawer title={editing ? 'Modifier semestre' : 'Nouveau semestre'} placement="right" width={480} onClose={() => setOpen(false)} open={open} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={onSubmit}>{editing ? 'Enregistrer' : 'Créer'}</button></div>}>
        <Form form={form} layout="vertical">
          <Form.Item name="nom" label="Nom" rules={[{ required: true }]}><Input placeholder="Fall / Spring / Summer" /></Form.Item>
          <Form.Item name="annee" label="Année" rules={[{ required: true }]}><Input type="number" placeholder="2025" /></Form.Item>
          <Form.Item name="range" label="Période" rules={[{ required: true, message: 'Sélectionnez la période' }]}>
            <DatePicker.RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
